/**
 * CSRF token management utilities
 * Provides centralized access to CSRF tokens and refresh functionality
 */

/**
 * Get a cookie value by name
 * @param name The cookie name
 * @returns The cookie value or null if not found
 */
function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Get the current CSRF token, preferring the XSRF-TOKEN cookie (updated on each response)
 * and falling back to the meta tag (may be stale after session regeneration)
 * @returns The CSRF token string, or throws if not found
 */
export function getCsrfToken(): string {
    // Prefer cookie token (always up-to-date) over meta tag (may be stale in SPAs)
    const cookieToken = getCookie('XSRF-TOKEN');
    if (cookieToken) {
        return cookieToken;
    }

    // Fallback to meta tag for compatibility
    const metaToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    if (!metaToken) {
        throw new Error('CSRF token not found. Please refresh the page.');
    }
    return metaToken;
}

/**
 * Set the CSRF token in the meta tag
 * @param token The new CSRF token to set
 */
export function setCsrfToken(token: string): void {
    const metaTag = document.querySelector('meta[name="csrf-token"]');
    if (metaTag) {
        metaTag.setAttribute('content', token);
    }
}

/**
 * Refresh the CSRF token from the server
 * @returns The new CSRF token
 */
export async function refreshCsrfToken(): Promise<string> {
    try {
        const response = await fetch('/csrf/refresh', {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
            credentials: 'same-origin',
        });

        if (!response.ok) {
            throw new Error(`Failed to refresh CSRF token: ${response.status}`);
        }

        const data = await response.json();
        const newToken = data.csrfToken;

        if (!newToken) {
            throw new Error('No CSRF token in refresh response');
        }

        setCsrfToken(newToken);
        return newToken;
    } catch (error) {
        console.error('Error refreshing CSRF token:', error);
        throw error;
    }
}
